import * as Cesium from "cesium";

export function getInvalidLaneletsWithReasons(viewer, currentDataSource) {
  if (!currentDataSource || !currentDataSource.entities) return [];

  const MAX_DISTANCE_METERS = 4.0;
  const CONNECT_RADIUS_METERS = 0.005;
  const ellipsoid = (viewer && viewer.scene && viewer.scene.globe && viewer.scene.globe.ellipsoid) || Cesium.Ellipsoid.WGS84;
  const RADIUS = ellipsoid.maximumRadius;

  function normalizeForCompare(str) {
    if (str === null || str === undefined) return "";
    return String(str).toLowerCase().replace(/\s+/g, "").replace(/[-_]+/g, "");
  }

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (!property) return undefined;
      if (typeof property.getValue === "function") {
        return property.getValue();
      }
      return property;
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (!property) return undefined;
      if (typeof property.getValue === "function") {
        return property.getValue();
      }
      return property;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object") {
        return obj[key];
      }
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) {
      return props[key];
    }

    return undefined;
  }

  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) {
          if (typeof property.getValue === "function") {
            idFromProps = property.getValue();
          } else {
            idFromProps = property;
          }
        }
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) {
          if (typeof property.getValue === "function") {
            idFromProps = property.getValue();
          } else {
            idFromProps = property;
          }
        }
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && obj.id !== undefined) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) {
      return idFromProps;
    }

    return entity.id;
  }

  function cartesianToFlatCartographic(cart) {
    if (!cart) return null;
    const c = ellipsoid.cartesianToCartographic(cart);
    if (!c) return null;
    c.height = 0;
    return c;
  }

  function horizontalDistanceFromCarto(c1, c2) {
    if (!c1 || !c2) return Number.POSITIVE_INFINITY;
    const dLat = c2.latitude - c1.latitude;
    const dLon = c2.longitude - c1.longitude;
    const cosLat = Math.cos((c1.latitude + c2.latitude) * 0.5);
    const x = RADIUS * dLon * cosLat;
    const y = RADIUS * dLat;
    return Math.sqrt(x * x + y * y);
  }

  function horizontalDistance(cart1, cart2) {
    const c1 = cartesianToFlatCartographic(cart1);
    const c2 = cartesianToFlatCartographic(cart2);
    return horizontalDistanceFromCarto(c1, c2);
  }

  function getPolylinePositionsFromEntity(entity, time) {
    if (!entity || !entity.polyline) return null;
    const polyline = entity.polyline;
    let positionsProp = polyline.positions || polyline._positions;
    if (!positionsProp) return null;

    let positions = null;

    if (typeof positionsProp.getValue === "function") {
      positions = positionsProp.getValue(time);
    } else if (Array.isArray(positionsProp)) {
      positions = positionsProp;
    } else if (Array.isArray(positionsProp._value)) {
      positions = positionsProp._value;
    }

    if (!Array.isArray(positions) || positions.length === 0) return null;
    return positions;
  }

  function getFirstAndLastPositions(entity, time) {
    const positions = getPolylinePositionsFromEntity(entity, time);
    if (!positions || positions.length === 0) return null;
    return { first: positions[0], last: positions[positions.length - 1] };
  }

  function buildIdToInfoMap(viewer, currentDataSource) {
    const map = new Map();
    if (!viewer || !viewer.dataSources) return map;

    const collection = viewer.dataSources;
    for (let i = 0; i < collection.length; i++) {
      const ds = collection.get(i);
      if (!ds || !ds.entities || ds === currentDataSource) continue;

      const layerName = ds.layerName;
      if (!layerName) continue;

      const layerNorm = normalizeForCompare(layerName);
      const entities = ds.entities.values || ds.entities;
      if (!Array.isArray(entities)) continue;

      for (let j = 0; j < entities.length; j++) {
        const entity = entities[j];
        if (!entity || !entity.properties) continue;
        const idValue = getPropertyValueFromProps(entity.properties, "id");
        if (idValue === null || idValue === undefined) continue;

        const key = String(idValue);
        if (!map.has(key)) {
          map.set(key, { layerNorm, entity });
        }
      }
    }

    return map;
  }

  function buildVertexIndex(viewer, time) {
    const grid = new Map();
    if (!viewer || !viewer.dataSources) {
      return { grid, cellAngle: 1 };
    }

    const cellAngle = CONNECT_RADIUS_METERS / RADIUS;

    const collection = viewer.dataSources;
    for (let i = 0; i < collection.length; i++) {
      const ds = collection.get(i);
      if (!ds || !ds.entities) continue;

      const entities = ds.entities.values || ds.entities;
      if (!Array.isArray(entities)) continue;

      for (let j = 0; j < entities.length; j++) {
        const entity = entities[j];
        if (!entity || !entity.polyline) continue;

        const positions = getPolylinePositionsFromEntity(entity, time);
        if (!positions) continue;

        for (let k = 0; k < positions.length; k++) {
          const pos = positions[k];
          const carto = cartesianToFlatCartographic(pos);
          if (!carto) continue;

          const gx = Math.round(carto.longitude / cellAngle);
          const gy = Math.round(carto.latitude / cellAngle);
          const key = gx + ":" + gy;

          let cell = grid.get(key);
          if (!cell) {
            cell = [];
            grid.set(key, cell);
          }

          cell.push({ entity, carto });
        }
      }
    }

    return { grid, cellAngle };
  }

  function isPointConnectedToOthers(targetCarto, vertexIndex, ownerEntitiesSet) {
    if (!targetCarto) return false;
    const { grid, cellAngle } = vertexIndex;
    if (cellAngle <= 0) return false;

    const gx = Math.round(targetCarto.longitude / cellAngle);
    const gy = Math.round(targetCarto.latitude / cellAngle);

    for (let ix = gx - 1; ix <= gx + 1; ix++) {
      for (let iy = gy - 1; iy <= gy + 1; iy++) {
        const key = ix + ":" + iy;
        const cell = grid.get(key);
        if (!cell) continue;

        for (let j = 0; j < cell.length; j++) {
          const v = cell[j];
          if (!v || !v.carto) continue;
          if (ownerEntitiesSet.has(v.entity)) continue;

          const d = horizontalDistanceFromCarto(targetCarto, v.carto);
          if (d <= CONNECT_RADIUS_METERS) return true;
        }
      }
    }

    return false;
  }

  const entities = currentDataSource.entities.values || currentDataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  const time = (viewer && viewer.clock && viewer.clock.currentTime) || Cesium.JulianDate.now();

  const idToInfoMap = buildIdToInfoMap(viewer, currentDataSource);
  const vertexIndex = buildVertexIndex(viewer, time);

  const result = [];
  const seenIds = new Set();

  // helper: check one side (center, left, right) all connected
  function checkSideConnectivity(laneletCarto, leftFirstCarto, leftLastCarto, rightFirstCarto, rightLastCarto, vertexIndex, ownerEntitiesSet) {
    if (!laneletCarto || !leftFirstCarto || !leftLastCarto || !rightFirstCarto) {
      return null;
    }

    const dLeftStart = horizontalDistanceFromCarto(laneletCarto, leftFirstCarto);
    const dLeftEnd = horizontalDistanceFromCarto(laneletCarto, leftLastCarto);
    const leftJoinCarto = dLeftStart <= dLeftEnd ? leftFirstCarto : leftLastCarto;

    const dRightStart = horizontalDistanceFromCarto(laneletCarto, rightFirstCarto);
    const dRightEnd = rightLastCarto ? horizontalDistanceFromCarto(laneletCarto, rightLastCarto) : Number.POSITIVE_INFINITY;
    const rightJoinCarto = dRightStart <= dRightEnd || !rightLastCarto ? rightFirstCarto : rightLastCarto;

    const centerConnected = isPointConnectedToOthers(laneletCarto, vertexIndex, ownerEntitiesSet);
    const leftConnected = isPointConnectedToOthers(leftJoinCarto, vertexIndex, ownerEntitiesSet);
    const rightConnected = isPointConnectedToOthers(rightJoinCarto, vertexIndex, ownerEntitiesSet);

    return {
      centerConnected,
      leftConnected,
      rightConnected,
    };
  }

  for (let i = 0; i < entities.length; i++) {
    const laneletEntity = entities[i];
    if (!laneletEntity || !laneletEntity.properties) continue;

    const props = laneletEntity.properties;
    const reasons = [];

    const leftType = getPropertyValueFromProps(props, "left_line_type");
    const rightType = getPropertyValueFromProps(props, "right_line_type");
    const leftId = getPropertyValueFromProps(props, "left_line_id");
    const rightId = getPropertyValueFromProps(props, "right_line_id");

    const hasLeftId = leftId !== null && leftId !== undefined && leftId !== "";
    const hasRightId = rightId !== null && rightId !== undefined && rightId !== "";
    const hasLeftType = leftType !== null && leftType !== undefined && leftType !== "";
    const hasRightType = rightType !== null && rightType !== undefined && rightType !== "";

    if (!hasLeftId) reasons.push("Missing left ID");
    if (!hasRightId) reasons.push("Missing right ID");
    if (!hasLeftType) reasons.push("Missing left type");
    if (!hasRightType) reasons.push("Missing right type");

    let leftInfo = null;
    let rightInfo = null;
    let leftTypeValid = false;
    let rightTypeValid = false;

    if (hasLeftId && hasLeftType) {
      leftInfo = idToInfoMap.get(String(leftId));
      if (!leftInfo) {
        reasons.push("Left ID not found");
      } else {
        const normType = normalizeForCompare(leftType);
        if (!leftInfo.layerNorm.includes(normType)) {
          reasons.push("Left type mismatch");
        } else {
          leftTypeValid = true;
        }
      }
    }

    if (hasRightId && hasRightType) {
      rightInfo = idToInfoMap.get(String(rightId));
      if (!rightInfo) {
        reasons.push("Right ID not found");
      } else {
        const normType = normalizeForCompare(rightType);
        if (!rightInfo.layerNorm.includes(normType)) {
          reasons.push("Right type mismatch");
        } else {
          rightTypeValid = true;
        }
      }
    }

    const laneletPos = getFirstAndLastPositions(laneletEntity, time);
    if (!laneletPos || !laneletPos.first || !laneletPos.last) {
      reasons.push("Lanelet geom invalid");
    }

    let leftPos = null;
    if (leftInfo && leftInfo.entity) {
      leftPos = getFirstAndLastPositions(leftInfo.entity, time);
      if (!leftPos || !leftPos.first || !leftPos.last) {
        reasons.push("Left geom invalid");
      }
    }

    let rightPos = null;
    if (rightInfo && rightInfo.entity) {
      rightPos = getFirstAndLastPositions(rightInfo.entity, time);
      if (!rightPos || !rightPos.first || !rightPos.last) {
        reasons.push("Right geom invalid");
      }
    }

    if (laneletPos && laneletPos.first) {
      if (rightInfo && rightInfo.entity && rightPos && rightPos.first && rightTypeValid) {
        const dRight = horizontalDistance(laneletPos.first, rightPos.first);
        if (dRight > MAX_DISTANCE_METERS) {
          reasons.push("Right too far");
        }
      }

      if (leftInfo && leftInfo.entity && leftPos && leftPos.first && leftPos.last && leftTypeValid) {
        const d1 = horizontalDistance(laneletPos.first, leftPos.first);
        const d2 = horizontalDistance(laneletPos.first, leftPos.last);
        const minD = Math.min(d1, d2);
        if (minD > MAX_DISTANCE_METERS) {
          reasons.push("Left too far");
        }
      }
    }

    // Connectivity check: at least one side (start or end) must have all 3 vertices connected
    if (laneletPos && laneletPos.first && laneletPos.last && leftPos && leftPos.first && leftPos.last && rightPos && rightPos.first && rightPos.last && rightTypeValid && leftTypeValid) {
      const laneletStartCarto = cartesianToFlatCartographic(laneletPos.first);
      const laneletEndCarto = cartesianToFlatCartographic(laneletPos.last);

      const leftFirstCarto = cartesianToFlatCartographic(leftPos.first);
      const leftLastCarto = cartesianToFlatCartographic(leftPos.last);
      const rightFirstCarto = cartesianToFlatCartographic(rightPos.first);
      const rightLastCarto = cartesianToFlatCartographic(rightPos.last);

      if (laneletStartCarto && laneletEndCarto && leftFirstCarto && leftLastCarto && rightFirstCarto && rightLastCarto) {
        const ownerEntitiesSet = new Set();
        ownerEntitiesSet.add(laneletEntity);
        if (leftInfo && leftInfo.entity) ownerEntitiesSet.add(leftInfo.entity);
        if (rightInfo && rightInfo.entity) ownerEntitiesSet.add(rightInfo.entity);

        const startSide = checkSideConnectivity(laneletStartCarto, leftFirstCarto, leftLastCarto, rightFirstCarto, rightLastCarto, vertexIndex, ownerEntitiesSet);

        const endSide = checkSideConnectivity(laneletEndCarto, leftFirstCarto, leftLastCarto, rightFirstCarto, rightLastCarto, vertexIndex, ownerEntitiesSet);

        let startOk = false;
        let endOk = false;

        if (startSide) {
          startOk = startSide.centerConnected && startSide.leftConnected && startSide.rightConnected;
        }

        if (endSide) {
          endOk = endSide.centerConnected && endSide.leftConnected && endSide.rightConnected;
        }

        // invalid if both sides fail to have all three connected
        if (!startOk && !endOk) {
          if (startSide) reasons.push("Start side open");
          if (endSide) reasons.push("End side open");
        }
      }
    }

    if (reasons.length > 0) {
      const id = getEntityId(laneletEntity);
      const dedupeKey = id ?? laneletEntity;
      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({ entity: laneletEntity, id, reasons });
      }
    }
  }

  return result;
}
